// Client-side image compression using native canvas — no dependencies.
// Downscales huge phone photos (S25 = 8-12MB) to ~1MB before upload.
// HEIC files are returned as-is because the browser can't decode them into a canvas —
// the server converts HEIC → JPEG on its side.

const MAX_DIMENSION = 1920;   // px — long-edge
const QUALITY = 0.82;
const TARGET_BYTES = 1_000_000; // ~1MB target

const HEIC_MIMES = new Set(["image/heic", "image/heif"]);
const isHeic = (f) => HEIC_MIMES.has(f.type) || /\.(heic|heif)$/i.test(f.name);

/**
 * Resize + re-encode an image File. Returns a new File (JPEG) or the original
 * when it's already small enough or is HEIC (server converts those).
 */
export async function compressImage(file) {
  if (isHeic(file)) return file;
  if (!file.type.startsWith("image/")) return file;
  if (file.size <= TARGET_BYTES && file.type === "image/jpeg") return file;

  const bmp = await createBitmap(file);
  const { width, height } = fitInside(bmp.width, bmp.height, MAX_DIMENSION);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bmp, 0, 0, width, height);
  bmp.close?.();

  const blob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", QUALITY)
  );
  if (!blob) return file;

  // If the "compressed" output is actually bigger, keep the original.
  if (blob.size >= file.size) return file;

  const newName = file.name.replace(/\.\w+$/, "") + ".jpg";
  return new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
}

async function createBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch { /* fall through to <img> path */ }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode();
    return img;
  } finally { URL.revokeObjectURL(url); }
}

function fitInside(w, h, max) {
  if (w <= max && h <= max) return { width: w, height: h };
  const scale = Math.min(max / w, max / h);
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}
